/* ============================================================================
   GRAZE — con vật đói thì TỰ ĐI TÌM CỎ.

   Trước đây bỏ đói là một cái đồng hồ đếm ngược, không hơn: con bò đứng giữa
   một bãi cỏ dày và chết đói sau bốn ngày. Loài `feed: null` thì ngược lại —
   mỗi đêm tự no lại một nửa từ hư không, kể cả khi cả nông trại đã lát nhựa.
   Cả hai đều là con số thay cho hành vi, và người chơi nhìn thấy ngay là sai.

   Ở đây cỏ trên bản đồ là THỨC ĂN THẬT:

   · Con vật đói nhắm tới bụi cỏ gần nhất và đi tới đó. Tới nơi thì ăn, và bụi
     cỏ BIẾN MẤT — nên một đàn đông sẽ gặm trụi khu quanh chuồng, và người chơi
     phải để chừa cỏ hoặc phải cắt cỏ tích rơm. Đó mới là một quyết định.
   · Hết cỏ thì đói tiếp, và quá `starveDays` thì chết — luật cũ giữ nguyên.
     Chết đói vẫn có thật, chỉ là giờ nó có nguyên nhân nhìn thấy được.

   Ai ăn được gì là do CONTENT nói, không phải `switch (id)`:

   · Loài có `feed` (bò/dê/cừu ăn rơm, heo/cá/chó ăn cỏ khô) tìm bụi cỏ nào RỤNG
     RA đúng thứ đó. Thêm một loại "cỏ ba lá" rụng rơm là bò tự biết ăn.
   · Loài `feed: null` (gà, vịt) mổ sâu trên nền cỏ THƯỜNG — không cần bụi nào
     cả. Vì thế chúng gần như không bao giờ chết đói, đúng như gà thả rông; chỉ
     chết khi quanh đó không còn một mảnh cỏ nào.
============================================================================ */

import type { AnimalDef, Content, Entity, GameState, Tile } from "./types.ts";
import type { Draft, MapView } from "./state.ts";
import { dEntity } from "./state.ts";
import { TILE, idx, blockedForActor, propDef } from "./world.ts";

/**
 * Đủ để đọc một lưới ô: `GameState` và `MapView` đều vừa khuôn này.
 *
 * Có nó vì bữa ăn ĐÊM phải đọc lưới của BẢN ĐỒ CON VẬT ĐANG Ở, không phải lưới
 * đang hiện trên màn hình. Người chơi ngủ trong nhà thì `state.tiles` là cái
 * nhà, và bản cũ đem toạ độ con gà ngoài ruộng đi hỏi lưới ấy — ra kết quả vô
 * nghĩa, im lặng.
 */
type Luoi = { readonly w: number; readonly h: number; readonly tiles: readonly Tile[] };

function oTai(v: Luoi, x: number, y: number): Tile | null {
  if (x < 0 || y < 0 || x >= v.w || y >= v.h) return null;
  return v.tiles[y * v.w + x] ?? null;
}

/** Ăn một bữa no được bao nhiêu phần của `fedMinutes`. Cố ý KHÔNG đầy: cho ăn
 *  bằng tay vẫn phải hơn hẳn việc tự gặm, nếu không thì máng cỏ vô nghĩa. */
const GRAZE_FILL = 0.7;

/** Bụi cỏ nào rụng ra MỘT TRONG các món loài này ăn. Duyệt content chứ không
 *  liệt kê id bằng tay, nên thêm "cỏ ba lá" rụng rơm là bò tự biết ăn. */
function propsDropping(content: Content, feeds: readonly string[]): Set<string> {
  const out = new Set<string>();
  if (!feeds.length) return out;
  const want = new Set(feeds);
  for (const id of content.propOrder) {
    const p = content.props[id];
    if (!p?.drops) continue;
    for (const dr of p.drops) if (want.has(dr.id)) out.add(id);
  }
  return out;
}

/** Ô (x,y) có ăn được với loài này không. */
export function grazeableAt(
  s: Luoi,
  content: Content,
  def: AnimalDef,
  x: number,
  y: number,
): boolean {
  const t = oTai(s, x, y);
  if (!t) return false;
  // Ruộng đã cày hay đang có cây thì KHÔNG phải bãi chăn — con bò gặm luống rau
  // là thứ người chơi sẽ nhớ rất lâu, theo nghĩa xấu.
  if (t.tilled || t.crop) return false;
  /* Hai đường ăn tự nhiên, KHÔNG loại trừ nhau:
       · bụi cỏ nào rụng ra thứ nó ăn được  → gặm bụi đó (bụi biến mất)
       · `pecks` (gà, vịt)                  → mổ sâu trên nền cỏ trống
     Trước đây gộp làm một qua `feed: null`, nên cho gà ăn cám được là lập tức
     mất luôn khả năng tự kiếm ăn của nó. */
  if (t.prop !== null) return propsDropping(content, def.feed).has(t.prop);
  if (def.pecks) return t.g === "grass" && t.b === null;
  return false;
}

/**
 * Ô ăn được GẦN NHẤT quanh con vật, hoặc null.
 *
 * Quét theo vòng từ trong ra và dừng ngay ở vòng đầu tìm thấy: con vật đói thì
 * đi tới bụi gần nhất, không đi tìm bụi ngon nhất. Trần `radius` để một con kẹt
 * trong chuồng không quét cả bản đồ mỗi bước.
 */
export function nearestGraze(
  s: Luoi,
  content: Content,
  def: AnimalDef,
  e: Entity,
  radius: number,
): { x: number; y: number } | null {
  const cx = Math.floor(e.x / TILE);
  const cy = Math.floor(e.y / TILE);
  if (grazeableAt(s, content, def, cx, cy)) return { x: cx, y: cy };
  for (let r = 1; r <= radius; r++) {
    for (let dy = -r; dy <= r; dy++)
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= s.w || y >= s.h) continue;
        if (grazeableAt(s, content, def, x, y)) return { x, y };
      }
  }
  return null;
}

/**
 * Cho con vật ở chỉ số `i` ăn ô nó đang đứng. Trả true nếu ăn được.
 *
 * Bụi cỏ bị ăn thì MẤT. Nền cỏ thường (gà vịt mổ sâu) thì không — sâu thì mọc
 * lại, còn bãi cỏ thì phải chờ mọc, và chính chỗ khác nhau đó là lý do nuôi bò
 * khó hơn nuôi gà.
 */
export function grazeHere(d: Draft, content: Content, v: MapView, i: number): boolean {
  const cur = d.s.entities[i];
  if (!cur || cur.map !== v.id) return false;
  const def = content.animals[cur.def];
  if (!def) return false;
  const x = Math.floor(cur.x / TILE);
  const y = Math.floor(cur.y / TILE);
  if (!grazeableAt(v, content, def, x, y)) return false;

  /* Ăn BỤI thì bụi mất; mổ sâu trên nền cỏ trống thì không mất gì — sâu mọc
     lại, còn bãi cỏ thì phải chờ mọc, và chính chỗ khác nhau đó là lý do nuôi
     bò khó hơn nuôi gà. Điều kiện là "ô có bụi không", chứ không phải "loài
     này có ăn được đồ cho ăn không": từ khi gà cũng ăn cám thì hai câu đó
     không còn trùng nhau nữa. */
  const t = oTai(v, x, y);
  if (t && t.prop !== null) {
    const m = v.edit(y * v.w + x);
    if (!m) return false;
    m.prop = null;
    m.hp = 0;
  }

  const e = dEntity(d, i);
  if (!e) return false;
  e.animal.fed = Math.max(e.animal.fed, def.fedMinutes * GRAZE_FILL);
  e.animal.hungryDays = 0;
  return true;
}

/**
 * Một lần đi ăn ĐÊM, chạy lúc sang ngày.
 *
 * Cần có vì người chơi ngủ là cả đêm trôi qua trong một action — không có khung
 * hình nào để con vật đi tới bụi cỏ. Không có bước này thì cứ ngủ vài đêm là cả
 * đàn chết đói dù nông trại đầy cỏ, tức là ngủ trở thành thứ trừng phạt.
 *
 * CHỈ loài `housing: "free"` (gà, vịt, chó) mới đi. Con có chuồng — bò, dê,
 * cừu, heo, cá — thì KHÔNG BAO GIỜ bị dời nữa, dù đói tới đâu.
 *
 * Đây là lỗi mà người chơi nhìn thấy còn tôi thì mất ba lần mới tìm ra gốc: hàm
 * này gán thẳng `e.x`/`e.y` trong bán kính 14 ô, mà ruột chuồng lát bê tông nên
 * bên trong không có gì gặm được — nên đêm nào cả đàn cũng bị bốc qua rào ra
 * ngoài. Đường sống của con có chuồng giờ là CÁI MÁNG (`eatFromTroughNight`),
 * đúng như cái máng sinh ra để làm.
 *
 * Bán kính rộng hơn ban ngày: cả một đêm thì con vật đi được xa hơn nhiều so
 * với vài phút game.
 */
export function grazeNight(
  d: Draft,
  content: Content,
  v: MapView,
  i: number,
  radius = 14,
): boolean {
  const cur = d.s.entities[i];
  if (!cur || cur.map !== v.id) return false;
  const def = content.animals[cur.def];
  if (!def) return false;
  if (def.housing !== "free") return false;
  const spot = nearestGraze(v, content, def, cur, radius);
  if (!spot) return false;

  // Dời con vật tới đúng bãi nó vừa ăn: sáng ra người chơi thấy nó đứng ở chỗ
  // bãi cỏ vừa biến mất, và câu chuyện tự khớp.
  const e = dEntity(d, i);
  if (!e) return false;
  e.x = spot.x * TILE + TILE / 2;
  e.y = spot.y * TILE + TILE / 2;
  e.ai.path = [];
  return grazeHere(d, content, v, i);
}

/** Chỉ số ô của một điểm ăn được, dùng làm đích cho A*. */
export function grazeGoal(s: GameState, spot: { x: number; y: number }): number {
  return idx(s.w, spot.x, spot.y);
}

/**
 * Chỗ TRÚ MƯA cho con vật thả rông: ô đứng được KỀ một gốc cây (vật thể
 * `tall`) gần nhất trong 8 ô. Quét vòng từ trong ra như `nearestGraze`, và
 * hỏi `blockedForActor` với đúng hộp của loài — trả về một ô mà nó không đứng
 * được là vỡ bất biến "không nằm trong ô đặc" ngay bước sau.
 *
 * Không có cây nào gần → null: con vật đứng yên tại chỗ (co ro giữa sân còn
 * hơn chạy đi tìm cây ở đầu kia nông trại trong mưa).
 */
export function shelterSpot(
  s: GameState,
  content: Content,
  e: Entity,
  box: { w: number; h: number },
): { x: number; y: number } | null {
  const cx = Math.floor(e.x / TILE);
  const cy = Math.floor(e.y / TILE);
  const keCay = (x: number, y: number): boolean => {
    for (const [dx, dy] of [
      [0, -1],
      [0, 1],
      [-1, 0],
      [1, 0],
    ] as [number, number][]) {
      const t = s.tiles[idx(s.w, x + dx, y + dy)];
      if (!t?.prop) continue;
      if (x + dx < 0 || y + dy < 0 || x + dx >= s.w || y + dy >= s.h) continue;
      if (propDef(content, t.prop)?.tall) return true;
    }
    return false;
  };
  const dungDuoc = (x: number, y: number): boolean => {
    const t = s.tiles[idx(s.w, x, y)];
    if (!t || t.g === "water" || t.tilled || t.crop) return false;
    return !blockedForActor(s, content, x * TILE + TILE / 2, y * TILE + TILE / 2, box.w, box.h, false);
  };
  if (keCay(cx, cy) && dungDuoc(cx, cy)) return { x: cx, y: cy };
  for (let r = 1; r <= 8; r++) {
    for (let dy = -r; dy <= r; dy++)
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= s.w || y >= s.h) continue;
        if (keCay(x, y) && dungDuoc(x, y)) return { x, y };
      }
  }
  return null;
}
